/**
 * NewsTickerStrip.tsx — N-3 News Headline Bonus
 *
 * Fixed top-of-screen strip (z:590, below modals).
 * Auto-dismisses after 5 seconds.
 * Effect was already applied by endTurn (store-side); this is display only.
 *
 * Mount: App.tsx as first child inside playing shell.
 * Gate: currentHeadline !== null.
 */

import type React from "react";
import { useEffect } from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./NewsTickerStrip.module.css";

type EffectKind =
  | "reward_renewables"
  | "reward_low_pollution"
  | "punish_fossils"
  | "global_bonus"
  | "flavor";

function effectBadge(effect: EffectKind): string {
  switch (effect) {
    case "reward_renewables":
      return "RENEWABLE BONUS";
    case "reward_low_pollution":
      return "CLEAN-AIR BONUS";
    case "punish_fossils":
      return "FOSSIL PENALTY";
    case "global_bonus":
      return "GLOBAL RELIEF";
    default:
      return "SDG NEWS";
  }
}

function stripClass(effect: EffectKind): string {
  switch (effect) {
    case "reward_renewables":
      return styles.stripRewardRenewables ?? "";
    case "reward_low_pollution":
      return styles.stripRewardLowPollution ?? "";
    case "punish_fossils":
      return styles.stripPunishFossils ?? "";
    case "global_bonus":
      return styles.stripGlobalBonus ?? "";
    default:
      return styles.stripFlavor ?? "";
  }
}

function badgeClass(effect: EffectKind): string {
  switch (effect) {
    case "reward_renewables":
      return styles.badgeRewardRenewables ?? "";
    case "reward_low_pollution":
      return styles.badgeRewardLowPollution ?? "";
    case "punish_fossils":
      return styles.badgePunishFossils ?? "";
    case "global_bonus":
      return styles.badgeGlobalBonus ?? "";
    default:
      return styles.badgeFlavor ?? "";
  }
}

export function NewsTickerStrip(): React.ReactElement | null {
  const currentHeadline = useGameStore((s) => s.currentHeadline);
  const dismissHeadline = useGameStore((s) => s.dismissHeadline);
  // Codex Phase 2a MED 1: hide while board-walk anim is in flight so the
  // ticker doesn't appear before the player visibly arrives at the new round.
  // Dismiss timer ALSO gated — it should only count down once the strip is
  // actually visible to the player.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);

  useEffect(() => {
    if (currentHeadline === null) return;
    if (boardAnimationActive) return;
    // R13 Codex-C HIGH: punish_fossils is handled by FossilPenaltyModal which
    // requires explicit Acknowledge — do not let the ticker's 5s dismiss timer
    // auto-clear the headline out from under it.
    if (currentHeadline.effect === "punish_fossils") return;
    const t = window.setTimeout(dismissHeadline, 5000);
    return () => window.clearTimeout(t);
  }, [currentHeadline, dismissHeadline, boardAnimationActive]);

  if (currentHeadline === null) return null;
  if (boardAnimationActive) return null;
  // R13 Bug #8: punish_fossils now shown as a centered modal (FossilPenaltyModal).
  // Skip ticker strip for that effect to avoid double-display.
  if (currentHeadline.effect === "punish_fossils") return null;

  const { title, body, effect } = currentHeadline;
  const eff = effect as EffectKind;

  return (
    <div
      className={`${styles.strip} ${stripClass(eff)}`}
      // Click-anywhere-to-dismiss restored after R15 AW conversion of dismiss
      // hint to a button — the playtest pattern of "tap the headline to dismiss"
      // should still work. Inner button stopPropagation prevents double-fire.
      onClick={dismissHeadline}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") dismissHeadline();
      }}
      // biome-ignore lint/a11y/useSemanticElements: <output> is a form element; role="status" on div is the correct live-region pattern
      role="status"
      aria-live="polite"
      data-testid="news-ticker-strip"
      data-effect={effect}
    >
      <span className={`${styles.badge} ${badgeClass(eff)}`}>{effectBadge(eff)}</span>
      <span className={styles.title}>{title}</span>
      <span className={styles.body}>{body}</span>
      {/* R15 Builder-AW — keyboard-accessible dismiss button for AT users */}
      <button
        type="button"
        className={styles.dismiss}
        onClick={(e) => {
          e.stopPropagation();
          dismissHeadline();
        }}
        aria-label="Dismiss news"
      >
        [×]
      </button>
    </div>
  );
}
